import { useCallback, useRef } from "react";

/**
 * Hidden dev cheat — fires when the user taps a target N times within a time
 * window. Returns a single callback to attach to an `onClick`. Caller decides
 * what happens when the cheat fires (e.g. unlock all modes).
 *
 * Matches the iOS implementation: 7 rapid taps on the "Level X" label within
 * 2 seconds. Per the day-1 spec it's undocumented; the only mention lives in
 * source-code comments and is never surfaced in the UI.
 */
export function useCheatCode(
  onFire: () => void,
  threshold = 7,
  windowMs = 2000,
): () => void {
  const tapCountRef = useRef(0);
  const firstTapRef = useRef<number | null>(null);

  return useCallback(() => {
    const now = Date.now();
    const first = firstTapRef.current;
    if (first !== null && now - first > windowMs) {
      tapCountRef.current = 0;
      firstTapRef.current = null;
    }
    if (tapCountRef.current === 0) {
      firstTapRef.current = now;
    }
    tapCountRef.current += 1;

    if (tapCountRef.current >= threshold) {
      tapCountRef.current = 0;
      firstTapRef.current = null;
      onFire();
    }
  }, [onFire, threshold, windowMs]);
}
